import {
  Alert,
  TableBase,
  TableBody,
  TableCell,
  TableHeaderCell,
  TableRow,
  TextSmallRegular,
  TextSmallSemiBold
} from '@hm/fundamental'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {BulkUploadPlantActionsFailure, BulkUploadPlantActionsGroupedRowError} from '../../types'
import {BULK_UPLOAD_HEADER_ROW_NUMBER} from '../../utils'

type Props = {
  failure: BulkUploadPlantActionsFailure
  'data-test-id': string
}

const RowErrorsTable: React.FC<{
  rows: BulkUploadPlantActionsGroupedRowError[]
  'data-test-id': string
}> = ({rows, 'data-test-id': dataTestId}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  return (
    <div
      className="max-h-[240px] overflow-y-auto rounded-md border border-border"
      data-test-id={dataTestId}
    >
      <TableBase>
        <thead>
          <TableRow>
            <TableHeaderCell className="w-80px whitespace-nowrap">
              {t('pomAction.bulkUpload.label.row')}
            </TableHeaderCell>
            <TableHeaderCell>{t('pomAction.bulkUpload.label.reason')}</TableHeaderCell>
          </TableRow>
        </thead>
        <TableBody>
          {rows.map(({rowNumber, reasons}) => (
            <TableRow key={rowNumber} data-test-id={`${dataTestId}-row-${rowNumber}`}>
              <TableCell className="align-top whitespace-nowrap">
                <TextSmallSemiBold>
                  {rowNumber === BULK_UPLOAD_HEADER_ROW_NUMBER
                    ? t('pomAction.bulkUpload.label.headerRow')
                    : rowNumber}
                </TextSmallSemiBold>
              </TableCell>
              <TableCell>
                <ul className="m-0 flex list-disc flex-col gap-4px pl-16px">
                  {reasons.map((reason, index) => (
                    <li key={index} className="break-words">
                      <TextSmallRegular>{reason}</TextSmallRegular>
                    </li>
                  ))}
                </ul>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </TableBase>
    </div>
  )
}

export const BulkUploadPlantActionsErrors: React.FC<Props> = ({
  failure,
  'data-test-id': dataTestId
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  switch (failure.kind) {
    case 'rows':
      return (
        <div className="flex flex-col gap-3">
          <Alert variant="error" data-cy={dataTestId}>
            <div className="flex flex-col gap-1">
              <TextSmallSemiBold>{t('pomAction.bulkUpload.message.invalidFile')}</TextSmallSemiBold>
              <TextSmallRegular>
                {t('pomAction.bulkUpload.message.rowErrorsSummary', {
                  errorCount: failure.errorCount,
                  rowCount: failure.rows.length
                })}
              </TextSmallRegular>
            </div>
          </Alert>
          <RowErrorsTable rows={failure.rows} data-test-id={`${dataTestId}-table`} />
        </div>
      )
    case 'detail':
      return (
        <Alert variant="error" data-cy={dataTestId}>
          <div className="flex flex-col gap-1">
            <TextSmallSemiBold>{t('pomAction.bulkUpload.message.invalidFile')}</TextSmallSemiBold>
            {failure.detail && <TextSmallRegular>{failure.detail}</TextSmallRegular>}
          </div>
        </Alert>
      )
    case 'unauthorized':
      return (
        <Alert variant="error" data-cy={dataTestId}>
          <TextSmallRegular>{t('pomAction.bulkUpload.message.unauthorized')}</TextSmallRegular>
        </Alert>
      )
    case 'forbidden':
      return (
        <Alert variant="error" data-cy={dataTestId}>
          <TextSmallRegular>{t('pomAction.bulkUpload.message.forbidden')}</TextSmallRegular>
        </Alert>
      )
    case 'unexpected':
      return (
        <Alert variant="error" data-cy={dataTestId}>
          <TextSmallRegular>{t('pomAction.bulkUpload.message.uploadFailed')}</TextSmallRegular>
        </Alert>
      )
  }
}
